import { Directive, HostListener, Input } from '@angular/core';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { FormattingService, parsePipePattern } from '@vwd/ngx-i18n';
import { KeyCode } from '../../utils/ngx-utils';

// all allowed keys (digits, decimal and grouping separators, signs)
const DISALLOWED_KEYS = /[^0123456789.,\u00a0 +\-]/;
const ALLOWED_KEY_CODES = [
  KeyCode.DELETE, KeyCode.BACKSPACE,
  KeyCode.TAB, KeyCode.ESC, KeyCode.ENTER,
  KeyCode.NUMPAD_DECIMAL, KeyCode.NUMPAD_SUBTRACT, KeyCode.NUMPAD_ADD, KeyCode.DASH,
  KeyCode.HOME, KeyCode.END, KeyCode.RIGHT, KeyCode.LEFT,
  KeyCode.F1, KeyCode.F2, KeyCode.F3, KeyCode.F4, KeyCode.F5, KeyCode.F6, KeyCode.F7,
  KeyCode.F8, KeyCode.F9, KeyCode.F10, KeyCode.F11, KeyCode.F12, KeyCode.F13, KeyCode.F14,
  KeyCode.F15
];

const COPY_PASTE_KEY_CODES = [KeyCode.A, KeyCode.C, KeyCode.V, KeyCode.X, KeyCode.INSERT, KeyCode.DELETE];


/**
 * Example:
 *    <input type="text" OnlyNumber="true">
 *    <input type="text" OnlyNumber="plus minus decimals grouping">
 *    <input type="text" OnlyNumber="none">
 *
 * OnlyNumber also picks up on an additional number directive,
 * to limit the number of decimals you can type:
 *
 *    <input type="text" number="1.0-3" OnlyNumber="true">
*/
@Directive({
  // tslint:disable-next-line:directive-selector
  selector: 'input[OnlyNumber],textarea[OnlyNumber]'
})
export class OnlyNumberDirective {
  private onlyNumberOn = true;
  private numberDirectiveOptions: string;
  private maximumFractionDigits: number;

  @Input()
  get OnlyNumber() { return this.onlyNumberOn; }
  set OnlyNumber(value: string | boolean) {
    this.onlyNumberOn = !!value;
    if (typeof value === 'string') {
      // reset all first
      this.parseOnlyNumber(value);
    } else {
      // set all to true/false
      this.allowMinus = this.allowPlus = this.allowDecimals = this.allowGrouping = !!value || value == null;
    }
    // if we have a number pattern with or without decimals, set allowDecimals accordingly,
    // overriding anything you set before
    if (this.maximumFractionDigits != null) {
      this.allowDecimals = this.maximumFractionDigits > 0;
    }
  }

  // piggyback on number value accessor
  @Input('number')
  get numberOptions() { return this.numberDirectiveOptions; }
  set numberOptions(value: string) {
    const parsedPipePattern = value && parsePipePattern(value);
    // evaluate pipe pattern to see how many decimals the [number]
    // directive will use
    if (parsedPipePattern) {
      this.maximumFractionDigits = parsedPipePattern.maximumFractionDigits;
      if (this.maximumFractionDigits != null) {
        this.allowDecimals = this.maximumFractionDigits > 0;
      }
    } else {
      this.maximumFractionDigits = undefined;
      // trigger allowDecimals validation, as we can flip back to false
      this.OnlyNumber = this.OnlyNumber;
    }
  }

  private allowMinus: boolean = true;
  private allowPlus: boolean = false;
  private allowDecimals: boolean = true;
  private allowGrouping: boolean = true;

  constructor(private formattingService: FormattingService) {
  }

  private parseOnlyNumber(value: string) {
    this.allowMinus = this.allowPlus = this.allowDecimals = this.allowGrouping = false;
    switch (value) {
      case 'false':
      case 'none':
        this.onlyNumberOn = false;
        break;
      case 'true':
      case 'any':
      case 'all':
      case '':
        this.onlyNumberOn = this.allowMinus = this.allowPlus = this.allowDecimals = this.allowGrouping = true;
        break;
      default:
        for (const name of (value as string).split(/\s+/)) {
          switch (name) {
            case 'minus':
              this.allowMinus = true;
              break;
            case 'plus':
              this.allowPlus = true;
              break;
            case 'decimals':
              this.allowDecimals = true;
              break;
            case 'grouping':
              this.allowGrouping = true;
              break;
            case '':
              break;
            default:
              throw new Error(`Invalid OnlyNumber value "${value}".`);
          }
        }
        this.onlyNumberOn = true;
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(e: ClipboardEvent) {
    if (!this.onlyNumberOn) {
      return;
    }

    // we'll take over the paste action
    e.preventDefault();

    const clipboardData = e.clipboardData;
    const input = e.target as HTMLInputElement;

    if (!clipboardData) {
      return;
    }

    const text = (clipboardData.getData('text') || clipboardData.getData('text/plain') || '')
      .trim().replace(DISALLOWED_KEYS, '');

    if (!text) {
      return;
    }

    // actually paste the text
    if (this.tryInsertText(input, text, 'paste')) {
      // trigger input events (for model binding etc.)
      input.dispatchEvent(new Event('input'));
      input.dispatchEvent(new Event('change'));
    }

  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    if (!this.onlyNumberOn) {
      return;
    }

    const input = e.target as HTMLInputElement;

    // check sign characters '-' and '+'; FormattingService does not allow filtering these
    if (e.key === '-' && !this.allowMinus || e.key === '+' && !this.allowPlus) {
      e.preventDefault();
      return;
    }

    // check whether inserting the specified character gives us an acceptable number string
    if (e.key && e.key.length === 1 && !(e.ctrlKey || e.metaKey || e.altKey)) {
      if (DISALLOWED_KEYS.test(e.key) || !this.tryInsertText(input, e.key, 'test')) {
        e.preventDefault();
      }
      return;
    }

    if (ALLOWED_KEY_CODES.includes(e.keyCode) ||
      ((e.ctrlKey || e.metaKey) && COPY_PASTE_KEY_CODES.includes(e.keyCode))) {
      // let it happen, don't do anything
      return;
    }

    // Prevent everything else and stop the keypress
    e.preventDefault();
  }

  /** Tries to insert the given input at the current caret position */
  private tryInsertText(input: HTMLInputElement, text: string, action: 'test' | 'paste'): boolean {
    // determine start and end of selection (if any)
    const start = Math.min(input.selectionStart, input.selectionEnd);
    const end = Math.max(input.selectionStart, input.selectionEnd);

    const newText = input.value.substr(0, start) + text + input.value.substr(end);

    // we should parse the future combined input to check for validity
    let shouldParse = true;

    // allow empty input, only +, - or decimal separator for when a user
    // starts typing "+1" or "-.5"; in this case we do not need to parse
    if (newText === ''
      || newText === '-' || newText === '+'
      // "." or "+."
      || (newText.length === 1 || newText.length === 2 && '-+'.includes(newText[0]))
        && ',.'.includes(newText[newText.length - 1]) // quick check, then slow check
        && newText[newText.length - 1] === getLocaleNumberSymbol(this.formattingService.localeId, NumberSymbol.Decimal)
    ) {
      shouldParse = false;
    }

    // what will we allow for the number we just created?
    const parseOptions = shouldParse && {
      allowDecimals: this.allowDecimals,
      allowGrouping: this.allowGrouping,
      allowMinus: this.allowMinus,
      allowPlus: this.allowPlus,
      maximumFractionalDigits: this.maximumFractionDigits,
      // only allow extra decimals when pasting, not when typing
      roundDecimals: action === 'paste',
    };

    // do we still have a valid number?
    if (shouldParse
      && isNaN(this.formattingService.parseNumber(newText, parseOptions))
      // should be supported in @vwd/ngx-i18n@1.0.11 directly
      // { allowDecimals: this.allowDecimals, allowGrouping: this.allowGrouping, allowMinus: this.allowMinus, allowPlus: this.allowPlus }
      || newText.includes('-') && !this.allowMinus
      || newText.includes('+') && !this.allowPlus) {
      // const newTextVisible = input.value.substr(0, start) + '<' + text + '>' + input.value.substr(end);
      // console.log(`Typing "${text}" would result in "${newTextVisible}", which is invalid.`);
      return false;
    } else {
      // when pasting, actually set the new input value
      // (event triggers are the responsibility of the caller)
      if (action === 'paste') {
        input.value = newText;
        input.setSelectionRange(start + text.length, start + text.length, <any>input.selectionDirection);
      }
      return true;
    }
  }
}

